/**
 * The way back to the top of a long scroll.
 *
 * Takes the scroller rather than finding one, because the app and every sheet have their own and a
 * component that guessed would pick the wrong one inside a modal. The threshold and its hysteresis
 * live in `core/scroll-top.ts`.
 */

import { useEffect, useState, type RefObject } from "react";
import { ArrowUp } from "lucide-react";
import { showsScrollTop } from "../../core/scroll-top";

export function ScrollTopButton({ targetRef, className }: { targetRef: RefObject<HTMLElement | null>; className: string }) {
	const [showing, setShowing] = useState(false);

	useEffect(() => {
		// Read here rather than during render: a ref is still null on the first render and attaches
		// before effects run, so a sheet that never re-renders would otherwise never wire this up.
		const scroller = targetRef.current;
		if (!scroller) {
			return;
		}
		const onScroll = () => setShowing((shown) => showsScrollTop(scroller.scrollTop, scroller.clientHeight, shown));
		onScroll();
		scroller.addEventListener("scroll", onScroll, { passive: true });
		return () => scroller.removeEventListener("scroll", onScroll);
	}, [targetRef]);

	if (!showing) {
		return null;
	}
	return (
		<button
			type="button"
			className={`toTop ${className}`}
			aria-label="Scroll to top"
			onClick={() => {
				targetRef.current?.scrollTo({ top: 0, behavior: "smooth" });
				setShowing(false);
			}}
		>
			<ArrowUp size={20} strokeWidth={2.25} aria-hidden="true" />
		</button>
	);
}
